import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  AppBar,
  Chip,
  Stack,
  Tooltip,
  TextField,
  IconButton,
} from '@mui/material';
import { get, cloneDeep } from 'lodash';
import Header from 'components/Header/Header';
import { WhiteBox } from 'components/WhiteBox/WhiteBox';
import CakeIcon from '@mui/icons-material/Cake';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';
import HomeIcon from '@mui/icons-material/Home';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ChurchIcon from '@mui/icons-material/Church';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import useAlerts from 'components/Alerts/useAlerts';
import { getPractitioner } from 'modules/Administration/PatientRegistry/Practitioners/_api';
import {
  getNameTranslated,
  getTelecomTranslated,
  getAddressTranslated,
} from 'modules/Administration/PatientRegistry/_grid';
import { getPatient, updatePatient } from './_api';
import { IPatient } from './_types';
import PatientStudiesGrid from './PatientStudiesGrid';
import PatientOrdersGrid from './PatientOrdersGrid';
import PractitionerAddDialog from './PractitionerAddDialog';
import PractitionerManageDialog from './PractitionerManageDialog';
import {
  getFamilyName,
  getGivenName,
  getBirthNumber,
  getPhones,
  getEmails,
  formatDate,
} from './_grid';
import { useWithTitle } from 'utils/hooks/useWithTitle';

const PatientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['Patients', 'PatientRegistry']);
  const navigate = useNavigate();
  const { setPageTitle } = useWithTitle();
  const { toggleLoader } = useAppGlobals();
  const { addErrorAlert } = useAlerts();

  const [patient, setPatient] = useState<IPatient | null>(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [activeAddressTab, setActiveAddressTab] = useState(0);
  const [practitioners, setPractitioners] = useState<any[]>([]);
  const [activePractitionerTab, setActivePractitionerTab] = useState(0);
  const [isAddPractitionerOpen, setIsAddPractitionerOpen] = useState(false);
  const [isManagePractitionersOpen, setIsManagePractitionersOpen] = useState(false);

  const [editField, setEditField] = useState<'phone' | 'email' | null>(null);
  const [fieldValue, setFieldValue] = useState('');

  const loadPatient = async () => {
    if (!id) {
      navigate('/patients');
      return;
    }

    toggleLoader(true);
    try {
      const data = await getPatient(Number(id));
      if (data === false) {
        addErrorAlert(t('Patients:loadPatientError'));
      } else {
        const patientData = data as IPatient;
        setPatient(patientData);

        const practitionerIds = get(patientData, 'generalPractitionerIds', []);
        if (practitionerIds.length > 0) {
          const results = await Promise.all(
            practitionerIds.map((id: number) => getPractitioner(id)),
          );
          const practitionerResults = results.filter(Boolean).map((p: any) => ({
            ...p,
            nameTranslated: getNameTranslated(p.name),
            telecomTranslated: getTelecomTranslated(p.telecom || [], t),
            addressTranslated: getAddressTranslated(p.address || []),
          }));
          setPractitioners(practitionerResults);

          if (activePractitionerTab >= practitionerResults.length) {
            setActivePractitionerTab(0);
          }
        } else {
          setPractitioners([]);
          setActivePractitionerTab(0);
        }
      }
    } catch (error) {
      addErrorAlert(t('Patients:loadPatientError'));
      console.error('Error loading patient:', error);
    } finally {
      toggleLoader(false);
    }
  };

  const handleUpdateContact = async () => {
    if (!patient) return;

    toggleLoader(true);
    try {
      const updatedPatient = cloneDeep(patient);
      if (!updatedPatient.telecom) updatedPatient.telecom = [];

      const system = editField === 'phone' ? 'phone' : 'email';
      const existingIndex = updatedPatient.telecom.findIndex((t: any) => t.system === system);

      if (existingIndex > -1) {
        updatedPatient.telecom[existingIndex].value = fieldValue;
      } else {
        updatedPatient.telecom.push({
          system,
          value: fieldValue,
          use: editField === 'phone' ? 'mobile' : 'home',
        });
      }

      const result = await updatePatient(updatedPatient);
      if (result) {
        setPatient(result);
        setEditField(null);
        setFieldValue('');
      } else {
        addErrorAlert(t('Patients:updatePatientError'));
      }
    } catch (error) {
      addErrorAlert(t('Patients:updatePatientError'));
      console.error('Error updating patient contact:', error);
    } finally {
      toggleLoader(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) {
        navigate('/patients');
        return;
      }
      toggleLoader(true);
      try {
        const data = await getPatient(Number(id));
        if (cancelled) return;
        if (data === false) {
          addErrorAlert(t('Patients:loadPatientError'));
        } else {
          const patientData = data as IPatient;
          setPatient(patientData);
          const practitionerIds = get(patientData, 'generalPractitionerIds', []);
          if (practitionerIds.length > 0) {
            const results = await Promise.all(
              practitionerIds.map((pid: number) => getPractitioner(pid)),
            );
            if (cancelled) return;
            const practitionerResults = results.filter(Boolean).map((p: any) => ({
              ...p,
              nameTranslated: getNameTranslated(p.name),
              telecomTranslated: getTelecomTranslated(p.telecom || [], t),
              addressTranslated: getAddressTranslated(p.address || []),
            }));
            setPractitioners(practitionerResults);
            if (activePractitionerTab >= practitionerResults.length) {
              setActivePractitionerTab(0);
            }
          } else {
            setPractitioners([]);
            setActivePractitionerTab(0);
          }
        }
      } catch (error) {
        if (!cancelled) addErrorAlert(t('Patients:loadPatientError'));
      } finally {
        if (!cancelled) toggleLoader(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const familyName = getFamilyName(patient?.name || []);
    const givenName = getGivenName(patient?.name || []);
    const name = `${familyName} ${givenName}`.trim();
    const detailTitle = t('Patients:detailTitle');
    setPageTitle(name ? `${name} - ${detailTitle}` : detailTitle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient]);

  const getPatientName = () => {
    if (!patient) return t('Patients:detailTitle');
    const familyName = getFamilyName(patient.name || []);
    const givenName = getGivenName(patient.name || []);
    return `${familyName} ${givenName}`.trim() || t('Patients:detailTitle');
  };

  const addresses = get(patient, 'address', []);
  const activeAddress = addresses[activeAddressTab] || null;

  const gender = (get(patient, 'gender') || '').toUpperCase();
  const genderIcon =
    gender === 'MALE' ? (
      <MaleIcon sx={{ color: '#2196f3', fontSize: '1.2rem' }} />
    ) : gender === 'FEMALE' ? (
      <FemaleIcon sx={{ color: '#e91e63', fontSize: '1.2rem' }} />
    ) : null;

  const phones = getPhones(get(patient, 'telecom', []));
  const emails = getEmails(get(patient, 'telecom', []));

  const DataChip: React.FC<{ children: React.ReactNode; icon?: React.ReactNode }> = ({
    children,
    icon,
  }) => (
    <Box
      sx={{
        bgcolor: '#f7f7f7',
        px: 1,
        py: 0.25,
        borderRadius: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
      }}
    >
      {icon}
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {children}
      </Typography>
    </Box>
  );

  return (
    <>
      <Header
        title={
          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
            {getPatientName()}
            {patient && (
              <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
                {!get(patient, 'active', true) && (
                  <Tooltip title={t('Patients:inactive')}>
                    <PersonOffIcon sx={{ color: '#d32f2f' }} fontSize="small" />
                  </Tooltip>
                )}
                {get(patient, 'deceasedBoolean') && (
                  <Tooltip title={t('Patients:deceased')}>
                    <ChurchIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                  </Tooltip>
                )}
              </Stack>
            )}
          </Box>
        }
        backUrl="/patients"
        editUrl={`/patients/edit/${id}?back=/patients/${id}`}
      />
      {patient && (
        <>
          <WhiteBox bottomMargin={true} topMargin={true}>
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flexWrap: 'wrap', rowGap: 1 }}
              >
                <DataChip>RČ: {getBirthNumber(get(patient, 'identifier', []))}</DataChip>

                <DataChip
                  icon={
                    <CakeIcon
                      sx={{
                        fontSize: '1rem',
                        color: 'text.secondary',
                      }}
                    />
                  }
                >
                  {formatDate(get(patient, 'birthDate'))}
                </DataChip>

                {genderIcon && (
                  <DataChip>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {genderIcon}
                      {t(`Patients:genderValue.${gender}`)}
                    </Stack>
                  </DataChip>
                )}

                {phones.length > 0 ? (
                  phones.map((p, i) => (
                    <DataChip
                      key={`phone-${i}`}
                      icon={<PhoneIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                    >
                      {p}
                    </DataChip>
                  ))
                ) : editField === 'phone' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhoneIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                    <TextField
                      size="small"
                      variant="standard"
                      placeholder={t('Patients:phone')}
                      value={fieldValue}
                      onChange={(e) => setFieldValue(e.target.value)}
                      autoFocus={true}
                      sx={{ width: 120 }}
                    />
                    <IconButton size="small" onClick={handleUpdateContact} color="primary">
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setEditField(null)}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Tooltip title={t('Patients:addPhone')}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditField('phone');
                        setFieldValue('');
                      }}
                      sx={{ p: 0 }}
                    >
                      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                        <AddIcon
                          sx={{
                            fontSize: '0.9rem',
                            color: 'primary.main',
                            position: 'absolute',
                            bottom: -2,
                            right: -4,
                            bgcolor: 'background.paper',
                            borderRadius: '50%',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </Box>
                    </IconButton>
                  </Tooltip>
                )}

                {emails.length > 0 ? (
                  emails.map((e, i) => (
                    <Box
                      key={`email-${i}`}
                      component="a"
                      href={`mailto:${e}`}
                      sx={{
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover .email-chip': {
                          color: 'primary.main',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      <DataChip
                        icon={<EmailIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                      >
                        <Box component="span" className="email-chip">
                          {e}
                        </Box>
                      </DataChip>
                    </Box>
                  ))
                ) : editField === 'email' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <EmailIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                    <TextField
                      size="small"
                      variant="standard"
                      placeholder={t('Patients:email')}
                      value={fieldValue}
                      onChange={(e) => setFieldValue(e.target.value)}
                      autoFocus={true}
                      sx={{ width: 180 }}
                    />
                    <IconButton size="small" onClick={handleUpdateContact} color="primary">
                      <CheckIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => setEditField(null)}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Tooltip title={t('Patients:addEmail')}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditField('email');
                        setFieldValue('');
                      }}
                      sx={{ p: 0 }}
                    >
                      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <EmailIcon sx={{ fontSize: '1.2rem', color: 'text.secondary' }} />
                        <AddIcon
                          sx={{
                            fontSize: '0.9rem',
                            color: 'primary.main',
                            position: 'absolute',
                            bottom: -2,
                            right: -4,
                            bgcolor: 'background.paper',
                            borderRadius: '50%',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </Box>
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              {activeAddress && (
                <Box>
                  {addresses.length > 1 && (
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      {addresses.map((address: any, index: number) => (
                        <Chip
                          key={index}
                          label={`${t('PatientRegistry:address')} ${index + 1}`}
                          onClick={() => setActiveAddressTab(index)}
                          color={activeAddressTab === index ? 'primary' : 'default'}
                          variant={activeAddressTab === index ? 'filled' : 'outlined'}
                          size="small"
                        />
                      ))}
                    </Stack>
                  )}
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <DataChip
                      icon={<HomeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                    >
                      {activeAddress.line ? activeAddress.line.join(', ') : ''}
                      {activeAddress.line && (activeAddress.city || activeAddress.postalCode)
                        ? ', '
                        : ''}
                      {activeAddress.city || ''}
                      {activeAddress.city && activeAddress.postalCode ? ' ' : ''}
                      {activeAddress.postalCode || ''}
                    </DataChip>
                  </Stack>
                </Box>
              )}
            </Stack>
          </WhiteBox>

          <WhiteBox bottomMargin={true}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                {t('Patients:attendingPhysicians')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Tooltip title={t('Patients:managePractitioners')}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setIsManagePractitionersOpen(true)}
                  >
                    <ManageAccountsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('Patients:addPractitioner')}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setIsAddPractitionerOpen(true)}
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Box>
              {practitioners.length > 0 ? (
                <>
                  {practitioners.length >= 3 ? (
                    <>
                      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                        {practitioners.map((p: any, index: number) => (
                          <Chip
                            key={index}
                            label={p.nameTranslated}
                            onClick={() => setActivePractitionerTab(index)}
                            color={activePractitionerTab === index ? 'primary' : 'default'}
                            variant={activePractitionerTab === index ? 'filled' : 'outlined'}
                            size="small"
                          />
                        ))}
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ flexWrap: 'wrap', rowGap: 1 }}
                      >
                        <DataChip
                          icon={<PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                        >
                          {practitioners[activePractitionerTab]?.nameTranslated}
                        </DataChip>
                        {practitioners[activePractitionerTab]?.telecomTranslated !== '-' && (
                          <DataChip
                            icon={<PhoneIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                          >
                            {practitioners[activePractitionerTab]?.telecomTranslated}
                          </DataChip>
                        )}
                        {practitioners[activePractitionerTab]?.addressTranslated !== '-' && (
                          <DataChip
                            icon={<HomeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                          >
                            {practitioners[activePractitionerTab]?.addressTranslated}
                          </DataChip>
                        )}
                      </Stack>
                    </>
                  ) : (
                    <Stack spacing={1.5}>
                      {practitioners.map((p: any, index: number) => (
                        <Stack
                          key={index}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ flexWrap: 'wrap', rowGap: 1 }}
                        >
                          <DataChip
                            icon={<PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                          >
                            {p.nameTranslated}
                          </DataChip>
                          {p.telecomTranslated !== '-' && (
                            <DataChip
                              icon={
                                <PhoneIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                              }
                            >
                              {p.telecomTranslated}
                            </DataChip>
                          )}
                          {p.addressTranslated !== '-' && (
                            <DataChip
                              icon={<HomeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />}
                            >
                              {p.addressTranslated}
                            </DataChip>
                          )}
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('Patients:noPractitioners')}
                </Typography>
              )}
            </Box>
          </WhiteBox>

          <AppBar position="static" color="default" sx={{ mt: 2, mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(event, value) => setActiveTab(value)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab value="orders" label={t('Patients:orders')} />
              <Tab value="studies" label={t('Patients:studies')} />
            </Tabs>
          </AppBar>
          {activeTab === 'studies' && (
            <PatientStudiesGrid
              key={`studies-${get(patient, 'updatedAt') || id}`}
              birthNumber={getBirthNumber(get(patient, 'identifier', []))}
              patientId={get(patient, 'id', '')?.toString()}
              updatedAt={get(patient, 'updatedAt')}
            />
          )}
          {activeTab === 'orders' && (
            <PatientOrdersGrid
              key={`orders-${get(patient, 'updatedAt') || id}`}
              birthNumber={getBirthNumber(get(patient, 'identifier', []))}
              patientId={get(patient, 'id', '')?.toString()}
              updatedAt={get(patient, 'updatedAt')}
            />
          )}
          <PractitionerAddDialog
            open={isAddPractitionerOpen}
            onClose={() => setIsAddPractitionerOpen(false)}
            patient={patient}
            onAdded={loadPatient}
          />
          <PractitionerManageDialog
            open={isManagePractitionersOpen}
            onClose={() => setIsManagePractitionersOpen(false)}
            practitioners={practitioners}
            patient={patient}
            onChanged={loadPatient}
          />
        </>
      )}
    </>
  );
};

export default PatientDetail;
